import { useState, useEffect, useCallback, useMemo } from "react";
import { fetchActivity, revertActivity } from "../utils/auth";

// Admin-only record of who changed what. Entries are written by the server —
// it diffs the incoming roster against the stored one on every save — so the
// log sees an edit whether or not the browser cooperates, and the actor is
// taken from the session rather than anything the client claims.
//
// Most entries can also be undone from here. The undo is worked out and applied
// on the server from the raw before/after values it kept beside the entry, and
// it is refused outright if anything it would overwrite has changed since — so
// an undo puts one change back rather than quietly rolling over later work.
// Nothing is erased: the undo is logged as its own entry, and the original is
// marked with who undid it.
const ACTION_STYLES = {
  created: "bg-green-100 text-green-800",
  updated: "bg-blue-100 text-blue-800",
  deleted: "bg-red-100 text-red-700",
  "bulk-change": "bg-purple-100 text-purple-700",
  reverted: "bg-amber-100 text-amber-800",
  "signed-in": "bg-gray-100 text-gray-500",
};

function actionStyle(action) {
  if (ACTION_STYLES[action]) return ACTION_STYLES[action];
  if (action.startsWith("account-")) return "bg-indigo-100 text-indigo-700";
  return "bg-gray-100 text-gray-600";
}

// "Today", "Yesterday", then the date — so a week of work reads as a story
// rather than a wall of timestamps.
function dayLabel(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unknown date";
  const today = new Date();
  const startOf = x => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(today) - startOf(d)) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

function timeLabel(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

// What clicking Undo will do, in the words of the thing it undoes. Undoing an
// addition is a deletion, so that one is worth a second look before it happens.
function undoPrompt(row) {
  const what = row.target ? ` (${row.target})` : "";
  if (row.action === "created") {
    return `Undo this addition? It removes what was added${what} again.`;
  }
  if (row.action === "bulk-change") {
    return `Undo all ${row.undoRecords} record${row.undoRecords === 1 ? "" : "s"} this changed?`;
  }
  return null;
}

export default function ActivityView({ onUndone, onNotify }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [who, setWho] = useState("All");
  const [expanded, setExpanded] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // A refused undo explains itself against the row it was asked for, where the
  // change it collided with is on screen — not in a toast that outlives it.
  const [refusal, setRefusal] = useState(null);

  const load = useCallback(async () => {
    try {
      setRows(await fetchActivity(500));
      setError(null);
    } catch (e) {
      setRows([]);
      setError(e.message || "Could not load the activity log.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function undo(row) {
    const prompt = undoPrompt(row);
    if (prompt && !window.confirm(prompt)) return;
    setBusyId(row.id);
    setRefusal(null);
    try {
      const { data, revision } = await revertActivity(row.id);
      // Hand the schedule the server now holds to the rest of the app, so this
      // tab isn't left saving over an undo it asked for itself.
      if (data) onUndone?.(data, revision);
      onNotify?.({ message: `Undone: ${row.summary}` });
      await load();
    } catch (e) {
      setRefusal({
        id: row.id,
        message: e.message || "Could not undo that change.",
        conflicts: e.conflicts || [],
        since: e.since || [],
      });
    } finally {
      setBusyId(null);
    }
  }

  const actors = useMemo(
    () => [...new Set((rows || []).map(r => r.actor).filter(Boolean))].sort(),
    [rows]
  );

  const visible = useMemo(
    () => (rows || []).filter(r => who === "All" || r.actor === who),
    [rows, who]
  );

  // Server returns newest first; group runs of the same day under one heading.
  const grouped = useMemo(() => {
    const out = [];
    visible.forEach(row => {
      const label = dayLabel(row.at);
      if (!out.length || out[out.length - 1].label !== label) out.push({ label, rows: [] });
      out[out.length - 1].rows.push(row);
    });
    return out;
  }, [visible]);

  if (rows === null) {
    return <p className="text-sm text-gray-500 p-6">Loading activity…</p>;
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Activity</h2>
          <p className="text-sm text-gray-500">
            Every change, who made it, and what it was before. Newest first, last 500 entries.
            Undo puts one change back on its own — anything written over since has to be sorted out
            first.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={who}
            onChange={e => setWho(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="All">Everyone</option>
            {actors.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <button
            onClick={load}
            className="text-sm border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          {error}
        </p>
      )}

      {visible.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
          <div className="text-3xl mb-2">🗒️</div>
          <p className="text-gray-500 text-sm">Nothing recorded yet.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.map(group => (
            <div key={group.label}>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                {group.label}
              </h3>
              <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
                {group.rows.map(row => {
                  const hasDetail = (row.changes || []).length > 0;
                  const open = expanded === row.id;
                  return (
                    <div key={row.id} className="px-4 py-2.5">
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <span className="text-xs text-gray-400 tabular-nums w-16 flex-shrink-0">
                          {timeLabel(row.at)}
                        </span>
                        <span className="text-sm font-medium text-gray-700">
                          {row.actorName || row.actor}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${actionStyle(row.action || "")}`}>
                          {row.action}
                        </span>
                        <span className="text-sm text-gray-600 flex-1 min-w-0">{row.summary}</span>
                        {hasDetail && (
                          <button
                            onClick={() => setExpanded(open ? null : row.id)}
                            className="text-xs text-blue-600 hover:text-blue-700 flex-shrink-0"
                          >
                            {open ? "Hide" : `${row.changes.length} field${row.changes.length === 1 ? "" : "s"}`}
                          </button>
                        )}
                        {row.undoneAt ? (
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            undone by {row.undoneBy || "someone"}
                          </span>
                        ) : row.canUndo ? (
                          <button
                            onClick={() => undo(row)}
                            disabled={busyId === row.id}
                            title={
                              row.undoRecords > 1
                                ? `Puts back the ${row.undoRecords} records this changed`
                                : "Puts this change back"
                            }
                            className="text-xs border border-gray-300 hover:bg-gray-50 text-gray-700 px-2 py-0.5 rounded flex-shrink-0 disabled:opacity-50 transition-colors"
                          >
                            {busyId === row.id
                              ? "Undoing…"
                              : row.undoRecords > 1 ? `Undo all ${row.undoRecords}` : "Undo"}
                          </button>
                        ) : null}
                      </div>

                      {refusal?.id === row.id && (
                        <div className="mt-2 ml-16 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          <p className="text-amber-800 font-medium">{refusal.message}</p>
                          {refusal.conflicts.length > 0 && (
                            <ul className="mt-1 text-amber-700 list-disc list-inside space-y-0.5">
                              {refusal.conflicts.map((c, i) => <li key={i}>{c}</li>)}
                            </ul>
                          )}
                          {refusal.since.length > 0 && (
                            <p className="mt-1 text-amber-700">
                              Touched since by {refusal.since.join(", ")}. Undo their change first, or put
                              this one back by hand.
                            </p>
                          )}
                          <button
                            onClick={() => setRefusal(null)}
                            className="mt-1 text-amber-700 hover:text-amber-900 underline"
                          >
                            Dismiss
                          </button>
                        </div>
                      )}

                      {open && (
                        <div className="mt-2 ml-16 overflow-x-auto">
                          <table className="text-xs text-gray-600 border-separate border-spacing-x-3">
                            <thead>
                              <tr className="text-gray-400">
                                <th className="text-left font-medium">Field</th>
                                <th className="text-left font-medium">Before</th>
                                <th className="text-left font-medium">After</th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.changes.map((c, i) => (
                                <tr key={`${c.field}-${i}`}>
                                  <td className="font-mono whitespace-nowrap">{c.field}</td>
                                  <td className="text-red-600">{c.before}</td>
                                  <td className="text-green-700">{c.after}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
